import type { TrueSolarResult } from '../types';

const pad2 = (value: number) => String(value).padStart(2, '0');

function parseDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new RangeError('Date must use YYYY-MM-DD.');
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    throw new RangeError('Date is invalid.');
  }
  return { year, month, day };
}

function parseTime(value: string) {
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) throw new RangeError('Time must use HH:MM or HH:MM:SS.');
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const second = Number(match[3] || '0');
  if (hour > 23 || minute > 59 || second > 59) throw new RangeError('Time is invalid.');
  return { hour, minute, second };
}

function timeToIndex(hour: number, minute: number, second = 0) {
  const seconds = hour * 3600 + minute * 60 + second;
  if (seconds >= 23 * 3600) return 12;
  if (seconds < 3600) return 0;
  return Math.floor((seconds + 3600) / 7200);
}

export function calculateTrueSolarTime({
  date,
  time,
  longitude,
  timezoneOffsetHours = 8,
}: {
  date: string;
  time: string;
  longitude: number;
  timezoneOffsetHours?: number;
}): TrueSolarResult {
  if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
    throw new RangeError('Longitude must be between -180 and 180 degrees.');
  }
  if (!Number.isFinite(timezoneOffsetHours) || timezoneOffsetHours < -14 || timezoneOffsetHours > 14) {
    throw new RangeError('UTC offset must be between -14 and +14 hours.');
  }

  const { year, month, day } = parseDate(date);
  const { hour, minute, second } = parseTime(time);
  const start = Date.UTC(year, 0, 1);
  const dayOfYear = Math.floor((Date.UTC(year, month - 1, day) - start) / 86_400_000) + 1;
  const daysInYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 366 : 365;
  const gamma = ((2 * Math.PI) / daysInYear) * (dayOfYear - 1 + (hour + minute / 60 + second / 3600 - 12) / 24);
  const equationOfTimeMinutes = 229.18 * (
    0.000075 +
    0.001868 * Math.cos(gamma) -
    0.032077 * Math.sin(gamma) -
    0.014615 * Math.cos(2 * gamma) -
    0.040849 * Math.sin(2 * gamma)
  );
  const longitudeCorrectionMinutes = 4 * longitude - 60 * timezoneOffsetHours;
  const totalCorrectionMinutes = equationOfTimeMinutes + longitudeCorrectionMinutes;
  const corrected = new Date(Date.UTC(year, month - 1, day, hour, minute, second) + Math.round(totalCorrectionMinutes * 60) * 1000);
  const correctedYear = corrected.getUTCFullYear();
  const correctedMonth = corrected.getUTCMonth() + 1;
  const correctedDay = corrected.getUTCDate();
  const correctedHour = corrected.getUTCHours();
  const correctedMinute = corrected.getUTCMinutes();
  const correctedSecond = corrected.getUTCSeconds();
  const originalMidnight = Date.UTC(year, month - 1, day);
  const correctedMidnight = Date.UTC(correctedYear, correctedMonth - 1, correctedDay);

  return {
    civilDate: date,
    civilTime: `${pad2(hour)}:${pad2(minute)}`,
    correctedDate: `${correctedYear}-${pad2(correctedMonth)}-${pad2(correctedDay)}`,
    correctedTime: `${pad2(correctedHour)}:${pad2(correctedMinute)}`,
    correctedTimeWithSeconds: `${pad2(correctedHour)}:${pad2(correctedMinute)}:${pad2(correctedSecond)}`,
    correctedHour,
    correctedMinute,
    correctedSecond,
    dayOffset: Math.round((correctedMidnight - originalMidnight) / 86_400_000),
    dayOfYear,
    equationOfTimeMinutes,
    longitudeCorrectionMinutes,
    totalCorrectionMinutes,
    timeIndex: timeToIndex(correctedHour, correctedMinute, correctedSecond),
  };
}
